import {useState, useEffect} from "react";
import {AnimatePresence, motion} from "framer-motion";
import BubbleBackground from "../../components/BubbleBackground";
import BirthdayProfileCard from "../../components/Celebrations/BirthdayProfileCard";
import BirthdayMessageCard from "../../components/Celebrations/BirthdayMessageCard";
import WorkAnniversaryProfileCard from "../../components/Celebrations/WorkAnniversaryProfileCard";
import WorkAnniversaryMessageCard from "../../components/Celebrations/WorkAnniversaryMessageCard";
import PaginationDots from "../../components/PaginationDots";
import Footer from "../../components/Footer";
import {getCelebrations} from "../../services/celebrationService";
import type {Celebration} from "../../services/celebrationService";

const SLIDE_INTERVAL = 15000;

const CelebrationsPage = () => {
    const [current, setCurrent] = useState(0);
    const [celebrations, setCelebrations] = useState<Celebration[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        getCelebrations()
            .then(setCelebrations)
            .catch(console.error)
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        if (celebrations.length === 0) return;

        const interval = setInterval(() => {
            setCurrent((prev) =>
                (prev + 1) % celebrations.length
            );
        }, SLIDE_INTERVAL);

        return () => clearInterval(interval);
    }, [celebrations.length]);

    const celebration = celebrations[current];

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-950 to-blue-900 text-white">
                <p className="text-xl text-blue-300">Loading celebrations...</p>
            </div>
        );
    }

    if (!celebration) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-950 to-blue-900 text-white">
                <p className="text-xl text-blue-300">No celebrations today.</p>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen lg:h-screen lg:overflow-hidden flex flex-col bg-gradient-to-b from-blue-950 to-blue-900 text-white relative">

            <BubbleBackground count={12}/>

            {/* Main Content */}
            <div className="flex-1 w-full px-4 md:px-8 pt-10 pb-4 flex flex-col">

                <AnimatePresence mode="wait">
                    <motion.div
                        key={celebration.id}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{
                            duration: 0.8,
                            ease: "easeInOut"
                        }}
                        className="flex flex-col lg:flex-row gap-8 w-full max-w-7xl mx-auto"
                    >
                        {/* Left Card */}
                        <div
                            className="w-full lg:w-[400px] rounded-3xl bg-blue-900/40 backdrop-blur-sm border border-blue-700/50 p-6">
                            {celebration.type === "birthday" ? (
                                <BirthdayProfileCard
                                    name={celebration.name}
                                    jobTitle={celebration.jobTitle}
                                    location={celebration.location}
                                    celebrationDate={celebration.celebrationDate}
                                    photoUrl={celebration.photoUrl}
                                />
                            ) : (
                                <WorkAnniversaryProfileCard
                                    name={celebration.name}
                                    jobTitle={celebration.jobTitle}
                                    location={celebration.location}
                                    celebrationDate={celebration.celebrationDate}
                                    yearsOfService={celebration.yearsOfService}
                                    photoUrl={celebration.photoUrl}
                                />
                            )}
                        </div>

                        {/* Right Card */}
                        <div
                            className="flex-1 rounded-3xl bg-blue-900/40 backdrop-blur-sm border border-blue-700/50 p-6">
                            {celebration.type === "birthday" ? (
                                <BirthdayMessageCard name={celebration.name}/>
                            ) : (
                                <WorkAnniversaryMessageCard name={celebration.name}/>
                            )}
                        </div>
                    </motion.div>
                </AnimatePresence>

                {/* Pagination Dots */}
                <div className="mt-6">
                    <PaginationDots
                        total={celebrations.length}
                        current={current}
                        onSelect={setCurrent}
                    />
                </div>

            </div>

            {/* Footer */}
            <Footer/>

        </div>
    );
};

export default CelebrationsPage;
